import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { useClients, useProjects } from '@/features/tasks/useTasks';
import type { WorkBlockSuggestion } from './useWorkBlocks';

interface WorkBlockPlannerProps {
  suggestions: WorkBlockSuggestion[];
  selected: WorkBlockSuggestion[];
  sprintBudgetMinutes: number;
  onChange: (items: WorkBlockSuggestion[]) => void;
  onConfirm: () => void;
  confirming?: boolean;
  lightDay: boolean;
  onLightDayChange: (v: boolean) => void;
  clientId: number | null;
  projectId: number | null;
  onClientChange: (clientId: number | null, projectId: number | null) => void;
}

function itemKey(item: WorkBlockSuggestion) {
  return `${item.task_id ?? ''}:${item.calendar_event_id ?? ''}`;
}

export function WorkBlockPlanner({
  suggestions,
  selected,
  sprintBudgetMinutes,
  onChange,
  onConfirm,
  confirming,
  lightDay,
  onLightDayChange,
  clientId,
  projectId,
  onClientChange,
}: WorkBlockPlannerProps) {
  const clients = useClients();
  const projects = useProjects(clientId ?? undefined);
  const selectedTotal = selected.reduce((s, i) => s + i.estimated_minutes, 0);
  const overBudget = selectedTotal > sprintBudgetMinutes;

  const toggle = (item: WorkBlockSuggestion) => {
    const key = itemKey(item);
    const exists = selected.some((s) => itemKey(s) === key);
    if (exists) {
      onChange(selected.filter((s) => itemKey(s) !== key));
    } else {
      onChange([...selected, item]);
    }
  };

  const all = [...suggestions];
  for (const s of selected) {
    if (!all.some((a) => itemKey(a) === itemKey(s))) {
      all.push(s);
    }
  }

  const meetings = all.filter((s) => s.required_meeting);
  const clientMatches = all.filter((s) => !s.required_meeting && s.client_match);
  const other = all.filter((s) => !s.required_meeting && !s.client_match);

  const renderGroup = (label: string, items: WorkBlockSuggestion[]) => {
    if (items.length === 0) return null;
    return (
      <div className="flex flex-col gap-1">
        <p className="px-1 text-label uppercase text-text-tertiary">{label}</p>
        <ul className="flex flex-col gap-1">
          {items.map((item) => {
            const key = itemKey(item);
            const checked = selected.some((s) => itemKey(s) === key);
            return (
              <li key={key}>
                <label
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2',
                    checked ? 'bg-accent-quiet' : 'hover:bg-surface-2',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(item)}
                    className="size-5 accent-accent"
                  />
                  <span className="min-w-0 flex-1 truncate text-body">{item.title}</span>
                  <span className="tabular shrink-0 text-small text-text-secondary">
                    {item.estimated_minutes}m
                    {item.required_meeting && ' · meeting'}
                    {item.pull_forward && ' · tomorrow'}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-border bg-surface-2 px-4 py-3">
        <input
          type="checkbox"
          checked={lightDay}
          onChange={(e) => onLightDayChange(e.target.checked)}
          className="size-5 accent-accent"
        />
        <span>
          <span className="block text-body font-medium">Light day</span>
          <span className="text-small text-text-secondary">15/5 sprints instead of 20/5</span>
        </span>
      </label>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-small font-medium text-text-secondary">Focus client</span>
          <select
            className="min-h-touch rounded-lg border border-border bg-surface-2 px-3 text-body"
            value={clientId ?? ''}
            onChange={(e) => {
              const next = e.target.value ? Number(e.target.value) : null;
              onClientChange(next, null);
            }}
          >
            <option value="">Any client</option>
            {(clients.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-small font-medium text-text-secondary">Project</span>
          <select
            className="min-h-touch rounded-lg border border-border bg-surface-2 px-3 text-body"
            value={projectId ?? ''}
            disabled={clientId == null}
            onChange={(e) => {
              const next = e.target.value ? Number(e.target.value) : null;
              onClientChange(clientId, next);
            }}
          >
            <option value="">Any project</option>
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="text-small text-text-tertiary">
        Tagging a client surfaces that backlog first when there is spare sprint time.
      </p>

      <div
        className={cn(
          'rounded-lg border px-4 py-3 text-small',
          overBudget ? 'border-warning bg-warning-quiet text-warning' : 'border-border bg-surface-2',
        )}
      >
        Selected {selectedTotal}m of {sprintBudgetMinutes}m sprint budget
        {overBudget && ', over budget, but you can still continue'}
        {selected.length === 0 && ' · start empty and add tasks as you go'}
      </div>

      {all.some((s) => s.pull_forward) && (
        <p className="text-small text-accent">
          Today&apos;s schedule is clear. Tasks marked &quot;tomorrow&quot; are pulled forward from
          the next day.
        </p>
      )}

      <div className="flex max-h-[min(50dvh,22rem)] flex-col gap-4 overflow-y-auto">
        {renderGroup('Meetings in this window', meetings)}
        {renderGroup(
          clientId != null ? 'Same client / project' : 'Suggested tasks',
          clientMatches.length > 0 ? clientMatches : other,
        )}
        {clientMatches.length > 0 && renderGroup('Other backlog', other)}
        {all.length === 0 && (
          <p className="px-1 text-small text-text-secondary">
            Nothing suggested: start empty and capture work during the block.
          </p>
        )}
      </div>

      <Button onClick={onConfirm} loading={confirming}>
        {selected.length === 0 ? 'Start empty block' : 'Start sprints'}
      </Button>
    </div>
  );
}
